import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Settings2, ChevronDown, ChevronRight, Info, Plus, Trash2, Percent } from 'lucide-react';
import { api } from '../../lib/api';

// -----------------------------------------------------------------------------
// Section 1 – Investimento inicial
// -----------------------------------------------------------------------------
const SECTION_AQUISICAO = [
  { path: 'acquisition_price_eur', label: 'Investimento inicial', prefix: '€', hint: 'Valor total previsto para o ponto de partida do plano (marca, ativos digitais, contratos e stocks associados).' },
  { path: 'working_capital_addition_eur', label: 'Fundo de maneio adicional', prefix: '€', hint: 'Capital necessário além do investimento inicial para financiar stocks, receitas em curso e primeiros meses de operação.' },
  { path: 'transaction_costs_eur', label: 'Custos de transação', prefix: '€', hint: 'Honorários legais, notariais e de due diligence associados à formalização do plano.' },
];

const SECTION_FISCALIDADE = [
  { path: 'vat_percent', label: 'IVA', suffix: '%', hint: 'Taxa geral de IVA em Portugal (2026: 23%). Usada para converter preços brutos em líquidos; todo o plano trabalha com valores líquidos.' },
  { path: 'tax_rate_percent', label: 'Imposto sobre lucro', suffix: '%', hint: 'Taxa prudente aplicada ao resultado tributável. IRC geral 19% em 2026; primeiros 50.000 € PME 15%; margem para derrama municipal.' },
  { path: 'cost_growth_percent', label: 'Crescimento de custos diretos', suffix: '%/ano', hint: 'Aumento anual do custo unitário de cada modelo (fornecedores e transporte).' },
  { path: 'fixed_costs_growth_percent', label: 'Crescimento de custos fixos', suffix: '%/ano', hint: 'Aumento anual das rubricas fixas (marketing, administração, eventos, operações).' },
  { path: 'variable_provision_growth_percent', label: 'Crescimento provisão variável', suffix: '%/ano', hint: 'Aumento anual da provisão variável por unidade vendida.' },
];

const SECTION_CUSTOS = [
  { path: 'fixed_costs_year_one_eur', label: 'Custos fixos ano 1', prefix: '€', hint: 'Total anual das rubricas fixas no primeiro ano. Cresce anualmente pela taxa acima.' },
  { path: 'variable_provision_per_unit_eur', label: 'Provisão variável por unidade', prefix: '€', hint: 'Reserva por cada equipamento vendido (garantias, assistência técnica, devoluções).' },
  { path: 'depreciation_eur', label: 'Amortizações anuais', prefix: '€', hint: 'Amortização sobre ativos incluídos no plano, deduzida antes do imposto.' },
  { path: 'cost_buffer_percent', label: 'Buffer sobre custo direto', suffix: '%', hint: 'Margem adicionada ao custo unitário para absorver variações cambiais e logísticas.' },
];

const SCENARIO_COLORS = ['#334155', '#059669', '#dc2626', '#7c3aed', '#0ea5e9', '#f59e0b', '#db2777'];

function fieldControl({ field, values, setValues }) {
  const raw = values[field.path];
  return (
    <div key={field.path} className="admin-field" data-testid={`admin-field-${field.path}`}>
      <div className="admin-field-label">
        <Label htmlFor={`f-${field.path}`}>
          {field.label}
          {field.prefix && <span className="admin-field-unit">({field.prefix})</span>}
          {field.suffix && <span className="admin-field-unit">({field.suffix})</span>}
        </Label>
        <span className="admin-field-hint" title={field.hint} aria-label={field.hint}>
          <Info size={12} />
        </span>
      </div>
      <Input
        id={`f-${field.path}`}
        type="number"
        step="0.01"
        value={raw ?? ''}
        onChange={(e) => setValues((v) => ({ ...v, [field.path]: e.target.value }))}
        data-testid={`admin-${field.path}`}
      />
      <p className="admin-field-desc">{field.hint}</p>
    </div>
  );
}

function Section({ title, subtitle, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="admin-section" data-testid={testId}>
      <button type="button" className="admin-section-head" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <div>
          <strong>{title}</strong>
          {subtitle && <small>{subtitle}</small>}
        </div>
      </button>
      {open && <div className="admin-section-body">{children}</div>}
    </section>
  );
}

// Sync gross/net using the VAT rate. Rounds to 2 decimals.
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export function AdminEditorBody({ config, onSaved, onClose }) {
  const [values, setValues] = useState({});
  const [products, setProducts] = useState([]);
  const [scenarios, setScenarios] = useState({});
  const [twelveMonth, setTwelveMonth] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [newScenario, setNewScenario] = useState({ open: false, key: '', label: '', description: '', color: SCENARIO_COLORS[2], price_growth_percent: 0, duplicate_from: '' });

  const allScalarFields = useMemo(
    () => [...SECTION_AQUISICAO, ...SECTION_FISCALIDADE, ...SECTION_CUSTOS],
    [],
  );

  useEffect(() => {
    if (config) {
      const next = {};
      allScalarFields.forEach((f) => { next[f.path] = config[f.path]; });
      setValues(next);
      setProducts(JSON.parse(JSON.stringify(config.products || [])));
      setScenarios(JSON.parse(JSON.stringify(config.scenarios || {})));
      setTwelveMonth(JSON.parse(JSON.stringify(config.twelve_month_plan || [])));
    }
  }, [config, allScalarFields]);

  const vat = Number(values.vat_percent) || 23;

  const save = async () => {
    setSubmitting(true);
    try {
      const body = {};
      allScalarFields.forEach((f) => {
        const v = values[f.path];
        body[f.path] = v === '' || v === null || v === undefined ? 0 : Number(v);
      });
      body.products = products.map((p) => ({
        ...p,
        price_net_eur: Number(p.price_net_eur) || 0,
        price_gross_eur: Number(p.price_gross_eur) || 0,
        cost_eur: Number(p.cost_eur) || 0,
        discount_percent: Math.max(0, Math.min(95, Number(p.discount_percent) || 0)),
      }));
      const scenariosBody = {};
      Object.entries(scenarios).forEach(([key, sc]) => {
        scenariosBody[key] = {
          ...sc,
          price_growth_percent: Number(sc.price_growth_percent) || 0,
          color: sc.color || SCENARIO_COLORS[0],
          years: (sc.years || []).map((y) => ({
            ...y,
            year: Number(y.year) || 0,
            units: Object.fromEntries(
              Object.entries(y.units || {}).map(([pid, u]) => [pid, Math.max(0, Math.round(Number(u) || 0))]),
            ),
          })),
        };
      });
      body.scenarios = scenariosBody;
      // Sanitise twelve_month_plan
      body.twelve_month_plan = twelveMonth.map((m, idx) => ({
        month: Number(m.month) || idx + 1,
        title: (m.title || '').slice(0, 80),
        focus: (m.focus || '').slice(0, 80),
        responsible: (m.responsible || '').slice(0, 60),
        actions: (m.actions || []).slice(0, 6).map((a) => String(a).slice(0, 160)).filter(Boolean),
      }));

      await api.put('/acquisition/config', body);
      toast.success('Pressupostos atualizados. Cenários, sensibilidade e hero foram recalculados.');
      window.dispatchEvent(new Event('lio-config-changed'));
      onSaved?.();
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Não foi possível guardar.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao guardar.');
    } finally {
      setSubmitting(false);
    }
  };

  // Bidirectional price sync (gross ↔ net) using the current VAT rate.
  const updateProductPrice = (idx, field, val) => {
    setProducts((arr) => arr.map((p, i) => {
      if (i !== idx) return p;
      const numeric = round2(val);
      if (field === 'price_gross_eur') {
        return { ...p, price_gross_eur: numeric, price_net_eur: round2(numeric / (1 + vat / 100)) };
      }
      if (field === 'price_net_eur') {
        return { ...p, price_net_eur: numeric, price_gross_eur: round2(numeric * (1 + vat / 100)) };
      }
      return { ...p, [field]: numeric };
    }));
  };

  const updateProduct = (idx, key, val) => {
    setProducts((arr) => arr.map((p, i) => (i === idx ? { ...p, [key]: val } : p)));
  };

  const updateScenarioField = (key, field, val) => {
    setScenarios((sc) => ({ ...sc, [key]: { ...sc[key], [field]: val } }));
  };
  const updateScenarioUnits = (scKey, yearIdx, productId, val) => {
    setScenarios((sc) => {
      const next = JSON.parse(JSON.stringify(sc));
      next[scKey].years[yearIdx].units[productId] = val;
      return next;
    });
  };

  const addScenario = async () => {
    if (!newScenario.key || !newScenario.label) {
      toast.error('Preencha a chave e o nome do cenário.');
      return;
    }
    if (!/^[a-z0-9_-]+$/.test(newScenario.key)) {
      toast.error('Chave inválida. Use apenas minúsculas, números, _ e -.');
      return;
    }
    if (scenarios[newScenario.key]) {
      toast.error('Já existe um cenário com essa chave.');
      return;
    }
    try {
      const { data } = await api.post('/acquisition/scenarios', {
        key: newScenario.key,
        label: newScenario.label,
        description: newScenario.description,
        color: newScenario.color,
        price_growth_percent: Number(newScenario.price_growth_percent) || 0,
        duplicate_from: newScenario.duplicate_from || null,
      });
      setScenarios((sc) => ({ ...sc, [data.key]: data.scenario }));
      setNewScenario({ open: false, key: '', label: '', description: '', color: SCENARIO_COLORS[2], price_growth_percent: 0, duplicate_from: '' });
      toast.success(`Cenário "${data.scenario.label}" adicionado.`);
      window.dispatchEvent(new Event('lio-config-changed'));
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível criar o cenário.');
    }
  };

  const deleteScenario = async (key) => {
    if (Object.keys(scenarios).length <= 1) {
      toast.error('É obrigatório manter pelo menos um cenário.');
      return;
    }
    if (!window.confirm(`Remover o cenário "${scenarios[key]?.label || key}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/acquisition/scenarios/${encodeURIComponent(key)}`);
      setScenarios((sc) => {
        const next = { ...sc };
        delete next[key];
        return next;
      });
      toast.success('Cenário removido.');
      window.dispatchEvent(new Event('lio-config-changed'));
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível remover.');
    }
  };

  const updateMonth = (idx, field, val) => {
    setTwelveMonth((arr) => arr.map((m, i) => (i === idx ? { ...m, [field]: val } : m)));
  };
  const updateMonthAction = (idx, actIdx, val) => {
    setTwelveMonth((arr) => arr.map((m, i) => {
      if (i !== idx) return m;
      const actions = [...(m.actions || [])];
      actions[actIdx] = val;
      return { ...m, actions };
    }));
  };
  const addMonthAction = (idx) => {
    setTwelveMonth((arr) => arr.map((m, i) => (i === idx ? { ...m, actions: [...(m.actions || []), ''] } : m)));
  };
  const removeMonthAction = (idx, actIdx) => {
    setTwelveMonth((arr) => arr.map((m, i) => {
      if (i !== idx) return m;
      return { ...m, actions: (m.actions || []).filter((_, k) => k !== actIdx) };
    }));
  };

  return (
        <div className="admin-editor-body" data-testid="admin-form" data-lenis-prevent>
          <Section title="1 · Investimento inicial" subtitle="Valor de partida, fundo de maneio e custos legais." testId="admin-section-aquisicao">
            <div className="admin-grid">
              {SECTION_AQUISICAO.map((f) => fieldControl({ field: f, values, setValues }))}
            </div>
          </Section>

          <Section title="2 · Fiscalidade e crescimento" subtitle="IVA em Portugal (23%), imposto sobre lucro e taxas de crescimento anual." testId="admin-section-fiscalidade">
            <div className="admin-grid">
              {SECTION_FISCALIDADE.map((f) => fieldControl({ field: f, values, setValues }))}
            </div>
          </Section>

          <Section title="3 · Custos operacionais" subtitle="Custos fixos, provisão variável, amortizações e buffer." testId="admin-section-custos">
            <div className="admin-grid">
              {SECTION_CUSTOS.map((f) => fieldControl({ field: f, values, setValues }))}
            </div>
          </Section>

          <Section title="4 · Produtos" subtitle="Preço bruto (com IVA) ↔ líquido sincronizados via IVA. O preço já inclui o desconto documental; o toggle aplica um desconto ADICIONAL (simulação)." testId="admin-section-produtos" defaultOpen={false}>
            {products.length === 0 && <p className="admin-empty">Sem produtos configurados.</p>}
            {products.map((p, idx) => {
              const priceNet = Number(p.price_net_eur) || 0;
              const cost = Number(p.cost_eur) || 0;
              const discountPct = Number(p.discount_percent) || 0;
              const marginEur = priceNet - cost;
              const marginPct = priceNet > 0 ? (marginEur / priceNet) * 100 : null;
              const discountedNet = priceNet * (1 - discountPct / 100);
              const marginDiscEur = discountedNet - cost;
              const marginDiscPct = discountedNet > 0 ? (marginDiscEur / discountedNet) * 100 : null;
              const marginClass = marginEur < 0 ? 'negative' : marginPct != null && marginPct < 15 ? 'thin' : 'ok';
              return (
                <div key={p.id} className="admin-product-row" data-testid={`admin-product-${p.id}`}>
                  <div className="admin-product-name">
                    <strong>{p.name}</strong>
                    <small>id: {p.id}</small>
                    {p.confirmed === false && <em className="admin-product-flag" title="Preço/custo por validar">Por validar</em>}
                  </div>
                  <div className="admin-product-fields">
                    <label>
                      <span>Preço bruto (€, com IVA)</span>
                      <Input
                        type="number" step="0.01"
                        value={p.price_gross_eur ?? ''}
                        onChange={(e) => updateProductPrice(idx, 'price_gross_eur', e.target.value)}
                        data-testid={`admin-product-${p.id}-gross`}
                      />
                    </label>
                    <label>
                      <span>Preço líquido (€, sem IVA)</span>
                      <Input
                        type="number" step="0.01"
                        value={p.price_net_eur ?? ''}
                        onChange={(e) => updateProductPrice(idx, 'price_net_eur', e.target.value)}
                        data-testid={`admin-product-${p.id}-net`}
                      />
                    </label>
                    <label>
                      <span>Custo unitário (€)</span>
                      <Input
                        type="number" step="0.01"
                        value={p.cost_eur ?? ''}
                        onChange={(e) => updateProductPrice(idx, 'cost_eur', e.target.value)}
                        data-testid={`admin-product-${p.id}-cost`}
                      />
                    </label>
                    <label>
                      <span><Percent size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> Desconto (%)</span>
                      <Input
                        type="number" step="0.1" min="0" max="95"
                        value={p.discount_percent ?? 0}
                        onChange={(e) => updateProduct(idx, 'discount_percent', e.target.value)}
                        data-testid={`admin-product-${p.id}-discount`}
                      />
                    </label>
                  </div>
                  <div className={`admin-product-margin admin-product-margin--${marginClass}`} data-testid={`admin-product-${p.id}-margin`}>
                    <div>
                      <span>Margem base (desc. documental)</span>
                      <strong data-testid={`admin-product-${p.id}-margin-pct`}>
                        {marginPct == null ? '—' : `${marginPct.toFixed(1)}%`}
                      </strong>
                    </div>
                    <div>
                      <span>Margem c/ desc. adicional</span>
                      <strong data-testid={`admin-product-${p.id}-margin-pct-discount`}>
                        {marginDiscPct == null ? '—' : `${marginDiscPct.toFixed(1)}%`}
                      </strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </Section>

          <Section title="5 · Cenários" subtitle="Nome, cor, crescimento anual do preço e vendas por ano. Adicione tantos cenários quanto precisar." testId="admin-section-cenarios">
            {Object.entries(scenarios).map(([key, sc]) => (
              <div key={key} className="admin-scenario" data-testid={`admin-scenario-${key}`}>
                <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', background: sc.color || SCENARIO_COLORS[0], flexShrink: 0, boxShadow: '0 0 0 2px #fff, 0 0 0 3px var(--line)' }} />
                    <div>
                      <strong>{sc.label || key}</strong>
                      <small>{sc.description}</small>
                    </div>
                  </div>
                  {Object.keys(scenarios).length > 1 && (
                    <button type="button" className="admin-scenario-remove" onClick={() => deleteScenario(key)} data-testid={`admin-scenario-${key}-remove`} title="Remover cenário">
                      <Trash2 size={14} /> Remover
                    </button>
                  )}
                </header>
                <div className="admin-scenario-grid">
                  <label>
                    <span>Nome visível</span>
                    <Input type="text" value={sc.label ?? ''} onChange={(e) => updateScenarioField(key, 'label', e.target.value)} data-testid={`admin-scenario-${key}-label`} />
                  </label>
                  <label>
                    <span>Crescimento de preço (%/ano)</span>
                    <Input type="number" step="0.1" value={sc.price_growth_percent ?? 0} onChange={(e) => updateScenarioField(key, 'price_growth_percent', e.target.value)} data-testid={`admin-scenario-${key}-growth`} />
                  </label>
                  <label>
                    <span>Cor (para tabs e gráficos)</span>
                    <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                      <input type="color" value={sc.color || SCENARIO_COLORS[0]} onChange={(e) => updateScenarioField(key, 'color', e.target.value)} style={{ width: 44, height: 32, border: '1px solid var(--line)', borderRadius: 6, padding: 2, background: '#fff' }} data-testid={`admin-scenario-${key}-color`} />
                      <div style={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap' }}>
                        {SCENARIO_COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => updateScenarioField(key, 'color', c)} aria-label={`Cor ${c}`} style={{ width: 20, height: 20, borderRadius: '50%', border: sc.color === c ? '2px solid var(--ink)' : '1px solid var(--line)', background: c, cursor: 'pointer', padding: 0 }} />
                        ))}
                      </div>
                    </div>
                  </label>
                  <label className="admin-scenario-desc">
                    <span>Descrição</span>
                    <textarea rows={2} value={sc.description ?? ''} onChange={(e) => updateScenarioField(key, 'description', e.target.value)} data-testid={`admin-scenario-${key}-description`} />
                  </label>
                </div>

                <table className="admin-scenario-table" data-testid={`admin-scenario-${key}-table`}>
                  <thead>
                    <tr>
                      <th>Ano</th>
                      {products.map((p) => <th key={p.id}>{p.name}</th>)}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sc.years || []).map((y, yIdx) => {
                      const total = Object.values(y.units || {}).reduce((s, u) => s + (Number(u) || 0), 0);
                      return (
                        <tr key={y.year}>
                          <th>Ano {y.year}</th>
                          {products.map((p) => (
                            <td key={p.id}>
                              <Input type="number" step="1" min="0" value={y.units?.[p.id] ?? 0} onChange={(e) => updateScenarioUnits(key, yIdx, p.id, e.target.value)} data-testid={`admin-scenario-${key}-y${y.year}-${p.id}`} />
                            </td>
                          ))}
                          <td className="admin-scenario-total">{total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}

            {!newScenario.open && (
              <button type="button" className="admin-scenario-add-btn" onClick={() => setNewScenario({ ...newScenario, open: true })} data-testid="admin-scenario-add">
                <Plus size={14} /> Adicionar cenário
              </button>
            )}
            {newScenario.open && (
              <div className="admin-scenario admin-scenario-new" data-testid="admin-scenario-new-form">
                <header><strong>Novo cenário</strong><small>Preencha os campos e guarde para adicioná-lo à lista.</small></header>
                <div className="admin-scenario-grid">
                  <label>
                    <span>Chave (id técnico, minúsculas)</span>
                    <Input type="text" value={newScenario.key} placeholder="p.ex. pessimistic" onChange={(e) => setNewScenario({ ...newScenario, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })} data-testid="admin-new-scenario-key" />
                  </label>
                  <label>
                    <span>Nome visível</span>
                    <Input type="text" value={newScenario.label} placeholder="p.ex. Pessimista" onChange={(e) => setNewScenario({ ...newScenario, label: e.target.value })} data-testid="admin-new-scenario-label" />
                  </label>
                  <label>
                    <span>Crescimento de preço (%/ano)</span>
                    <Input type="number" step="0.1" value={newScenario.price_growth_percent} onChange={(e) => setNewScenario({ ...newScenario, price_growth_percent: e.target.value })} data-testid="admin-new-scenario-growth" />
                  </label>
                  <label>
                    <span>Cor</span>
                    <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                      <input type="color" value={newScenario.color} onChange={(e) => setNewScenario({ ...newScenario, color: e.target.value })} style={{ width: 44, height: 32, border: '1px solid var(--line)', borderRadius: 6, padding: 2, background: '#fff' }} data-testid="admin-new-scenario-color" />
                      <div style={{ display: 'flex', gap: '.25rem' }}>
                        {SCENARIO_COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setNewScenario({ ...newScenario, color: c })} aria-label={`Cor ${c}`} style={{ width: 20, height: 20, borderRadius: '50%', border: newScenario.color === c ? '2px solid var(--ink)' : '1px solid var(--line)', background: c, cursor: 'pointer', padding: 0 }} />
                        ))}
                      </div>
                    </div>
                  </label>
                  <label>
                    <span>Duplicar vendas a partir de…</span>
                    <select value={newScenario.duplicate_from} onChange={(e) => setNewScenario({ ...newScenario, duplicate_from: e.target.value })} data-testid="admin-new-scenario-duplicate" style={{ padding: '.5rem .6rem', border: '1px solid var(--line)', borderRadius: 8, background: '#fff', fontSize: '.85rem' }}>
                      <option value="">Começar em zero</option>
                      {Object.entries(scenarios).map(([k, s]) => <option key={k} value={k}>{s.label || k}</option>)}
                    </select>
                  </label>
                  <label className="admin-scenario-desc">
                    <span>Descrição</span>
                    <textarea rows={2} value={newScenario.description} onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })} data-testid="admin-new-scenario-description" />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" onClick={() => setNewScenario({ ...newScenario, open: false })} data-testid="admin-new-scenario-cancel">Cancelar</Button>
                  <Button onClick={addScenario} data-testid="admin-new-scenario-save"><Plus size={14} /> Adicionar</Button>
                </div>
              </div>
            )}
          </Section>

          <Section title="6 · Plano de 12 meses" subtitle="Roadmap mensal para o primeiro ano de operação. Complementa o plano de 90 dias." testId="admin-section-twelve-month" defaultOpen={false}>
            <div className="admin-twelve-month-grid">
              {twelveMonth.map((m, idx) => (
                <div key={idx} className="admin-month-card" data-testid={`admin-month-${idx + 1}`}>
                  <header>
                    <span className="admin-month-number">Mês {m.month || idx + 1}</span>
                  </header>
                  <label>
                    <span>Título</span>
                    <Input type="text" value={m.title ?? ''} onChange={(e) => updateMonth(idx, 'title', e.target.value)} data-testid={`admin-month-${idx + 1}-title`} />
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                    <label>
                      <span>Foco</span>
                      <Input type="text" value={m.focus ?? ''} onChange={(e) => updateMonth(idx, 'focus', e.target.value)} data-testid={`admin-month-${idx + 1}-focus`} />
                    </label>
                    <label>
                      <span>Responsável</span>
                      <Input type="text" value={m.responsible ?? ''} onChange={(e) => updateMonth(idx, 'responsible', e.target.value)} data-testid={`admin-month-${idx + 1}-responsible`} />
                    </label>
                  </div>
                  <div className="admin-month-actions">
                    <span>Ações</span>
                    {(m.actions || []).map((a, actIdx) => (
                      <div key={actIdx} style={{ display: 'flex', gap: '.35rem' }}>
                        <Input type="text" value={a} onChange={(e) => updateMonthAction(idx, actIdx, e.target.value)} data-testid={`admin-month-${idx + 1}-action-${actIdx}`} />
                        <button type="button" className="admin-month-action-remove" onClick={() => removeMonthAction(idx, actIdx)} aria-label="Remover ação"><Trash2 size={12} /></button>
                      </div>
                    ))}
                    {(m.actions || []).length < 6 && (
                      <button type="button" className="admin-month-action-add" onClick={() => addMonthAction(idx)} data-testid={`admin-month-${idx + 1}-add-action`}>
                        <Plus size={12} /> Adicionar ação
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <div className="admin-footer-bar">
            <p className="admin-footer-note">
              <Info size={14} /> Guardar aplica as alterações à base de dados, sincroniza o pitch e recalcula todo o plano.
            </p>
            <div className="admin-footer-actions">
              <Button variant="ghost" onClick={() => onClose?.()} data-testid="admin-cancel">Cancelar</Button>
              <Button onClick={save} disabled={submitting} data-testid="admin-save">
                {submitting ? 'A guardar…' : 'Guardar alterações'}
              </Button>
            </div>
          </div>
        </div>
  );
}

export function AdminEditor({ open, onOpenChange, config, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-editor-dialog" data-testid="admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 size={20} /> Editor de pressupostos e cenários
          </DialogTitle>
          <DialogDescription>
            Fonte única de todos os cartões, tabelas e gráficos deste plano. Guardar recalcula tudo e sincroniza o pitch.
          </DialogDescription>
        </DialogHeader>
        <AdminEditorBody config={config} onSaved={onSaved} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
